import { UseQueryOptions, useQuery } from '@tanstack/react-query'

import { UseVacanciesQueryOptions } from '@/features/careers/v2/hooks/useJobs/types'
import { useService } from '@/hooks/useService'
import { CareersService } from '../../../services/CareersService'
import { useEffect, useState } from 'react'
import {
  addJobCountToDepartments,
  filterJobsByDepartments,
  filterJobsByLocations,
  mapJobsToArticles,
} from '@/features/careers/v2/hooks/useJobs/helpers'
import { GetJobsResponse } from '@/features/careers/v2/types'
/**
 * Shared hook that fetches raw jobs data without transformation
 * This ensures all components get the same base data
 */
export const useRawJobs = (
  queryOptions: Partial<UseQueryOptions<GetJobsResponse>> = {},
) => {
  const careers = useService(new CareersService('/careers/v2'))

  return useQuery<GetJobsResponse>({
    queryKey: ['jobs', 'raw'],
    queryFn: async () => {
      const { data } = await careers.getJobs<GetJobsResponse>()

      return data
    },
    staleTime: 60 * 60 * 1000,
    ...queryOptions,
  })
}

/**
 * useJobs is a custom hook that fetches vacancies data
 * from the our API and returns it in a paginated manner.
 *
 * @param {object} parameters - The parameters for the vacancies query.
 * @param {number} parameters.targetPageSize - The target number of items per page. Defaults to 6.
 * @param {number[]} parameters.filterDepartments - The department IDs to filter vacancies by.
 * @param {string[]} parameters.filterLocations - The location names to filter vacancies by.
 * @param {object} options - Additional options for the useQuery hook.
 *
 * @returns {object} The result of the useInfiniteQuery hook.
 */
export const useJobs = ({
  targetPageSize = 6,
  filterDepartments,
  filterLocations = [],
  articles,
  ...queryOptions
}: UseVacanciesQueryOptions) => {
  const [endCursor, setEndCursor] = useState(targetPageSize)

  // Use the shared raw jobs hook
  const rawJobsResult = useRawJobs(queryOptions)

  useEffect(() => {
    if (
      (filterDepartments && filterDepartments.length > 0) ||
      (filterLocations && filterLocations.length > 0)
    ) {
      setEndCursor(targetPageSize)
    }
  }, [filterDepartments, filterLocations])

  // Transform raw data locally
  const jobs = mapJobsToArticles(rawJobsResult.data ?? [], articles ?? [])
  const departments = addJobCountToDepartments(jobs)

  const filteredByDepartments = filterJobsByDepartments(jobs, filterDepartments)

  const filteredJobs = filterJobsByLocations(
    filteredByDepartments,
    filterLocations,
  )

  const sortedJobs = filteredJobs.sort((a, b) => {
    return (
      new Date(b.updated_at ?? '').getTime() -
      new Date(a.updated_at ?? '').getTime()
    )
  })

  const paginatedJobs = sortedJobs.slice(0, endCursor) ?? []

  const locations = new Set<string>()
  filteredByDepartments.forEach((job) => {
    if (job.location?.name) {
      locations.add(job.location.name)
    }
  })

  return {
    ...rawJobsResult,
    data: {
      jobs: paginatedJobs,
      departments,
      total: filteredJobs.length,
      locations: Array.from(locations),
      allJobs: filteredByDepartments,
    },
    loadMore: () => setEndCursor(endCursor + targetPageSize),
    hasNextPage: paginatedJobs.length < filteredJobs.length,
  }
}
